import {
    Box,
    Button,
    Card,
    CardContent,
    CardMedia,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Grid,
    TextField,
    Typography,
    TablePagination,
} from "@mui/material";
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { collection, deleteDoc, doc, getDocs, query, orderBy } from "firebase/firestore";
import { firestore } from "../../..";
import { useFeedback } from "../../../providers/feedback/feedback";
import { Edit, Delete, Search } from "@mui/icons-material";

interface NewsItem {
    docId: string;  // Firestore document ID
    id: any;        // Original timestamp ID from data
    title: string;
    subtitle: string;
    description: string;
    thumbnail: string;
    postedBy: string;
    createdAt: any;
    gallery?: string[];
    deanerName?: { id: Number; name: string } | null;
    selectedParishName?: { id: Number; value: string } | null;
}

function EditNewsTab() {
    const navigate = useNavigate();
    const { showSnackbar } = useFeedback();
    const [newsList, setNewsList] = useState<NewsItem[]>([]);
    const [filteredNews, setFilteredNews] = useState<NewsItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [newsToDelete, setNewsToDelete] = useState<NewsItem | null>(null);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(9);

    useEffect(() => {
        fetchNews();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (searchTerm.trim() === "") {
            setFilteredNews(newsList);
        } else {
            const filtered = newsList.filter(
                (news) =>
                    news.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    news.subtitle.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    news.postedBy.toLowerCase().includes(searchTerm.toLowerCase())
            );
            setFilteredNews(filtered);
        }
        setPage(0); // Reset to first page when search changes
    }, [searchTerm, newsList]);

    const fetchNews = async () => {
        try {
            setLoading(true);
            const newsQuery = query(
                collection(firestore, "news"),
                orderBy("createdAt", "desc")
            );
            const querySnapshot = await getDocs(newsQuery);

            const newsData: NewsItem[] = [];
            querySnapshot.forEach((doc) => {
                const data = doc.data();
                newsData.push({
                    docId: doc.id,  // Firestore document ID
                    ...data         // Spread the rest of the data
                } as NewsItem);
            });

            setNewsList(newsData);
            setFilteredNews(newsData);
        } catch (error) {
            console.error("Error fetching news:", error);
            showSnackbar("Error loading news");
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteClick = (news: NewsItem) => {
        setNewsToDelete(news);
        setDeleteDialogOpen(true);
    };

    const handleDeleteConfirm = async () => {
        if (!newsToDelete) return;

        try {
            await deleteDoc(doc(firestore, "news", newsToDelete.id));
            showSnackbar("News deleted successfully");
            setDeleteDialogOpen(false);
            setNewsToDelete(null);
            fetchNews();
        } catch (error) {
            console.error("Error deleting news:", error);
            showSnackbar("Error deleting news");
        }
    };

    const handleDeleteCancel = () => {
        setDeleteDialogOpen(false);
        setNewsToDelete(null);
    };

    const handleEditClick = (newsId: string) => {
        console.log("Edit button clicked - News Document ID:", newsId);
        console.log("Navigating to:", `/admin/news/edit/${newsId}`);
        navigate(`/admin/news/edit/${newsId}`);
    };

    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    // Calculate paginated data
    const paginatedNews = filteredNews.slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage
    );

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box mt={3}>
            <Box mb={3}>
                <TextField
                    fullWidth
                    placeholder="Search by title, subtitle, or posted by..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    InputProps={{
                        startAdornment: <Search sx={{ mr: 1, color: "text.secondary" }} />,
                    }}
                />
            </Box>

            {filteredNews.length === 0 ? (
                <Box textAlign="center" py={4}>
                    <Typography variant="h6" color="text.secondary">
                        {searchTerm ? "No news found matching your search" : "No news available"}
                    </Typography>
                </Box>
            ) : (
                <>
                    <Grid container spacing={3}>
                        {paginatedNews.map((news) => (
                            <Grid item xs={12} sm={6} md={4} key={news.docId}>
                                <Card
                                    sx={{
                                        height: "100%",
                                        display: "flex",
                                        flexDirection: "column",
                                        transition: "transform 0.2s",
                                        "&:hover": {
                                            transform: "translateY(-4px)",
                                            boxShadow: 3,
                                        },
                                    }}
                                >
                                    <CardMedia
                                        component="img"
                                        height="200"
                                        image={news.thumbnail}
                                        alt={news.title}
                                        sx={{ objectFit: "cover" }}
                                    />
                                    <CardContent sx={{ flexGrow: 1, pb: 1 }}>
                                        <Typography
                                            variant="h6"
                                            gutterBottom
                                            sx={{
                                                overflow: "hidden",
                                                textOverflow: "ellipsis",
                                                display: "-webkit-box",
                                                WebkitLineClamp: 2,
                                                WebkitBoxOrient: "vertical",
                                                fontWeight: 600,
                                                wordBreak: "break-word",
                                            }}
                                        >
                                            {news.title}
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                            sx={{
                                                overflow: "hidden",
                                                textOverflow: "ellipsis",
                                                display: "-webkit-box",
                                                WebkitLineClamp: 2,
                                                WebkitBoxOrient: "vertical",
                                                mb: 1,
                                                wordBreak: "break-word",
                                            }}
                                        >
                                            {news.subtitle}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            Posted by: {news.postedBy}
                                        </Typography>
                                        <br />
                                        <Typography variant="caption" color="text.secondary">
                                            {news.createdAt?.toDate
                                                ? new Date(news.createdAt.toDate()).toLocaleDateString()
                                                : "N/A"}
                                        </Typography>
                                    </CardContent>
                                    <Box
                                        sx={{
                                            display: "flex",
                                            flexDirection: { xs: "column", sm: "row" },
                                            p: 2,
                                            pt: 0,
                                            gap: 1,
                                        }}
                                    >
                                        <Button
                                            variant="outlined"
                                            startIcon={<Edit />}
                                            onClick={() => handleEditClick(news.docId)}
                                            sx={{ flex: 1, minWidth: 0, whiteSpace: "nowrap" }}
                                        >
                                            Edit
                                        </Button>
                                        <Button
                                            variant="outlined"
                                            color="error"
                                            startIcon={<Delete />}
                                            onClick={() => handleDeleteClick(news)}
                                            sx={{ flex: 1, minWidth: 0, whiteSpace: "nowrap" }}
                                        >
                                            Delete
                                        </Button>
                                    </Box>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>

                    {/* Pagination */}
                    <Box display="flex" justifyContent="center" mt={4}>
                        <TablePagination
                            component="div"
                            count={filteredNews.length}
                            page={page}
                            onPageChange={handleChangePage}
                            rowsPerPage={rowsPerPage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                            rowsPerPageOptions={[6, 9, 12, 18, 24]}
                            labelRowsPerPage="News per page:"
                        />
                    </Box>
                </>
            )}

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={deleteDialogOpen}
                onClose={handleDeleteCancel}
                aria-labelledby="delete-dialog-title"
                aria-describedby="delete-dialog-description"
            >
                <DialogTitle id="delete-dialog-title">Delete News</DialogTitle>
                <DialogContent>
                    <DialogContentText id="delete-dialog-description">
                        Are you sure you want to delete the news titled "
                        <strong>{newsToDelete?.title}</strong>"? This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleDeleteCancel} variant="outlined">
                        Cancel
                    </Button>
                    <Button onClick={handleDeleteConfirm} variant="contained" color="error" autoFocus>
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

export default EditNewsTab;
